import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import style from './tooltip.component.module.css';

interface TooltipProps {
    label: string;
    children: React.ReactElement;
    className?: string;
}

export const Tooltip = ({ label, children, className }: TooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<{ x: number; y: number; isBelow?: boolean } | null>(null);
    const [tooltipElement, setTooltipElement] = useState<HTMLDivElement | null>(null);
    const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

    // Check if tooltip is being cut off after it's rendered
    useEffect(() => {
        if (tooltipElement && position && isVisible && !position.isBelow && triggerRect) {
            const tooltipRect = tooltipElement.getBoundingClientRect();
            
            // Check if tooltip is cut off at the top
            if (tooltipRect.top < 2) {
                setPosition({
                    x: position.x,
                    y: triggerRect.bottom + 8,
                    isBelow: true
                });
            }
        }
    }, [tooltipElement, position, isVisible, triggerRect]);

    const handleMouseEnter = (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTriggerRect(rect);
        
        const viewportWidth = window.innerWidth;
        
        // Calculate initial position
        let x = rect.left + rect.width / 2;
        let y = rect.top - 8;
        
        // Estimate tooltip width for horizontal positioning
        const estimatedTooltipWidth = Math.min(label.length * 8, viewportWidth * 0.9);
        const tooltipHalfWidth = estimatedTooltipWidth / 2;
        
        // Adjust x position to keep tooltip within viewport
        if (x - tooltipHalfWidth < 10) {
            x = tooltipHalfWidth + 10;
        } else if (x + tooltipHalfWidth > viewportWidth - 10) {
            x = viewportWidth - tooltipHalfWidth - 10;
        }
        
        // Start with position above (will adjust after render if needed)
        setPosition({ x, y, isBelow: false });
        setIsVisible(true);
    };

    const handleMouseLeave = () => {
        setIsVisible(false);
        setPosition(null);
        setTriggerRect(null);
    };

    // Clone the children to add mouse event handlers
    const clonedChildren = React.cloneElement(children, {
        onMouseEnter: (e: React.MouseEvent) => {
            handleMouseEnter(e);
            // Call original onMouseEnter if it exists
            if (children.props.onMouseEnter) {
                children.props.onMouseEnter(e);
            }
        },
        onMouseLeave: (e: React.MouseEvent) => {
            handleMouseLeave();
            // Call original onMouseLeave if it exists
            if (children.props.onMouseLeave) {
                children.props.onMouseLeave(e);
            }
        }
    });

    // Create a custom tooltip that mimics the design system style
    const portalTooltip = isVisible && position ? createPortal(
        <div
            ref={setTooltipElement}
            className={`${style.tooltip}`}
            style={{
                left: position.x,
                top: position.y,
                transform: position.isBelow 
                    ? 'translateX(-50%) translateY(6px)' 
                    : 'translateX(-50%) translateY(-100%) translateY(-6px)',
            }}
            role="tooltip"
            aria-hidden="false"
        >
            {label}
            {/* Tooltip arrow */}
            {triggerRect && (
                <div
                    className={style.tooltipArrow}
                    style={{
                        top: position.isBelow ? '-6px' : '100%',
                        transform: `translateX(calc(-50% + ${(triggerRect.left + triggerRect.width / 2) - position.x}px))`,
                        ...(position.isBelow 
                            ? { borderBottom: '6px solid #161616' }
                            : { borderTop: '6px solid #161616' }
                        )
                    }}
                />
            )}
        </div>,
        document.body
    ) : null;

    return (
        <>
            {clonedChildren}
            {portalTooltip}
        </>
    );
};
